import { Check, Edit3, Loader2, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { AuthButton } from '@/components/ui';
import type { InventoryDetailItem } from '../types/inventoryDetail.types';
import { inventoryEditService } from '../../edit/services/inventoryEdit.service';

interface InventoryDetailSpecsProps {
  item: InventoryDetailItem;
  canUpdate: boolean;
  companyId?: string;
  userId?: string;
  onRefresh: () => Promise<any>;
}

export function InventoryDetailSpecs({ item, canUpdate, companyId, userId, onRefresh }: InventoryDetailSpecsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [specValues, setSpecValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (item && isEditing) {
      setSpecValues({
        height: item.height || '',
        internode_height: item.internode_height || '',
        canopy_diameter: item.canopy_diameter || '',
        trunk_diameter: item.trunk_diameter || '',
        base_diameter: item.base_diameter || '',
        pot_spec: item.pot_spec || '',
        substrate: item.substrate || '',
        water_requirement: item.water_requirement || '',
        light_requirement: item.light_requirement || '',
        origin_name: item.origin_name || '',
        seasonality: item.seasonality || '',
        expiry_date: item.expiry_date || '',
        note: item.note || '',
      });
    }
  }, [item, isEditing]);

  const handleSave = async () => {
    if (!item || !userId || !companyId) return;
    setIsSaving(true);
    try {
      await inventoryEditService.updateInventoryItem(
        item.id,
        {
          ...item,
          origin_name: specValues.origin_name,
          note: specValues.note,
          specifications: {
            height: specValues.height,
            internode_height: specValues.internode_height,
            canopy_diameter: specValues.canopy_diameter,
            trunk_diameter: specValues.trunk_diameter,
            base_diameter: specValues.base_diameter,
            pot_spec: specValues.pot_spec,
            substrate: specValues.substrate,
            water_requirement: specValues.water_requirement,
            light_requirement: specValues.light_requirement,
            seasonality: specValues.seasonality,
            expiry_date: specValues.expiry_date,
          }
        } as any,
        userId,
        companyId
      );
      await onRefresh();
      toast.success('Đã cập nhật thông số kỹ thuật thành công');
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save specs:', err);
      toast.error('Có lỗi xảy ra khi cập nhật thông số');
    } finally {
      setIsSaving(false);
    }
  };

  const specs = [
    { label: 'Chiều cao', key: 'height' },
    { label: 'Chiều cao lóng', key: 'internode_height' },
    { label: 'Đường kính tán', key: 'canopy_diameter' },
    { label: 'Đường kính thân', key: 'trunk_diameter' },
    { label: 'Đường kính gốc', key: 'base_diameter' },
    { label: 'Quy cách bầu', key: 'pot_spec' },
    { label: 'Giá thể', key: 'substrate' },
    { label: 'Nhu cầu nước', key: 'water_requirement' },
    { label: 'Ánh sáng', key: 'light_requirement' },
    { label: 'Xuất xứ', key: 'origin_name' },
    { label: 'Mùa vụ', key: 'seasonality' },
    { label: 'Hạn sử dụng', key: 'expiry_date' },
    { label: 'Ghi chú', key: 'note' },
  ];

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between border-l-4 border-emerald-600 pl-3">
        <h3 className="sm:text-sm text-xs font-semibold text-slate-900">Thông số kỹ thuật</h3>
        <div className="flex items-center gap-1">
          {isEditing ? (
            <>
              <button 
                onClick={handleSave}
                disabled={isSaving}
                className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-600 transition-colors"
                title="Lưu thay đổi"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </button>
              <button 
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600 transition-colors"
                title="Hủy bỏ"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <AuthButton 
              onClick={() => setIsEditing(true)}
              hasPermission={canUpdate}
              unauthorizedMessage="Bạn không có quyền chỉnh sửa thông số kỹ thuật."
              className="p-1.5 rounded-md hover:bg-slate-50 text-slate-400 hover:text-emerald-600 transition-colors bg-transparent border-none"
              title="Chỉnh sửa thông số"
            >
              <Edit3 className="h-4 w-4" />
            </AuthButton>
          )}
        </div>
      </div>
      
      <div className="space-y-1">
        {specs.map((spec) => (
          <div key={spec.label} className="grid grid-cols-[140px_1fr] gap-2 py-2.5 border-b border-slate-100 last:border-0 items-center">
            <span className="sm:text-sm text-xs text-slate-500">{spec.label}</span>
            {isEditing ? (
              spec.key === 'note' ? (
                <textarea 
                  value={specValues[spec.key] || ''}
                  onChange={(e) => setSpecValues({ ...specValues, [spec.key]: e.target.value })}
                  className="w-full rounded border border-slate-200 p-1.5 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none min-h-[60px]"
                />
              ) : spec.key === 'water_requirement' ? (
                <select 
                  value={specValues[spec.key] || ''}
                  onChange={(e) => setSpecValues({ ...specValues, [spec.key]: e.target.value })}
                  className="w-full rounded border border-slate-200 p-1.5 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">Chọn nhu cầu</option>
                  <option value="Thấp">Thấp</option>
                  <option value="Trung bình">Trung bình</option>
                  <option value="Cao">Cao</option>
                </select>
              ) : (
                <input 
                  type="text" 
                  value={specValues[spec.key] || ''}
                  onChange={(e) => setSpecValues({ ...specValues, [spec.key]: e.target.value })}
                  className="w-full rounded border border-slate-200 p-1.5 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                />
              )
            ) : (
              <span className="sm:text-sm text-xs text-slate-900 font-medium">{(item as any)[spec.key] || '-'}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
